<template>
  <div class="flex flex-col bg-surface-1 border border-border-separator rounded-md">
    <!-- Header. The switch sits beside the explanation rather than under it: opting in is the whole
         point of this card, and the copy is what someone reads to decide. -->
    <div
      class="flex items-center gap-2 px-3 py-2.5 min-h-[52px]"
      :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
      role="button"
      data-test="brief-header"
      :aria-expanded="expanded ? 'true' : 'false'"
      :tabindex="disabled ? -1 : 0"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <i class="ri-sun-line ri-125 flex-shrink-0" :class="disabled ? 'text-text-subtle' : 'text-text-link'" />
      <div class="flex items-center gap-1 flex-1">
        <span class="text-sm font-medium" :class="disabled ? 'text-text-subtle' : 'text-text-default'">
          {{ $t('messaging.briefOptIn.title') }}
        </span>
        <!-- The doc's ask, and the thing that makes this card not a form: Rey chooses what to look at.
             Stated before the toggle so nobody opts in expecting a box to type a question into. -->
        <span
          v-tooltip="$t('messaging.briefOptIn.explainer')"
          data-test="brief-explainer"
          tabindex="0"
          role="img"
          :aria-label="$t('messaging.briefOptIn.explainer')"
          class="flex text-text-subtle cursor-help"
          @click.stop
          @keydown.stop
        ><i class="ri-information-line" /></span>
      </div>
      <span v-if="!disabled" data-test="brief-status" class="text-xs text-text-subtle flex-shrink-0">
        {{ status_label }}
      </span>
      <i
        v-if="!disabled"
        class="ri-arrow-down-s-line ri-125 text-text-subtle transition-transform"
        :class="expanded ? 'rotate-180' : ''"
      />
    </div>

    <!-- The refusal explains itself where the control is, the same way ScheduledCreate's does. -->
    <div v-if="disabled" class="px-3 pb-2.5 text-xs text-text-subtle">
      {{ disabledReason }}
    </div>

    <div v-else-if="expanded" data-test="brief-body" class="bg-surface-3/60 border-t border-border-separator rounded-b-md">
    <div class="flex items-center gap-2 px-3 py-3">
      <span class="text-xs font-medium text-text-default flex-1">{{ $t('messaging.briefOptIn.toggleLabel') }}</span>
      <fusion-switch
        class="flex-shrink-0"
        :model-value="opted_in"
        :disabled="disabled || saving"
        @update:modelValue="setOptedIn"
      />
    </div>

    <!-- Schedule. Only once opted in: a time picker above an off switch is asking someone to configure
         something they have not agreed to have. -->
    <div v-if="opted_in" class="flex flex-col gap-3 px-3 pb-3">
      <!-- What the brief covers: one store, or a dealer group. The same choices, in the same order, as the
           chat composer's dealership filter — groups (All rooftops first) then dealerships — so a person
           finds here exactly what they find there. Hidden when there is nothing to choose between. -->
      <label v-if="show_coverage" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">{{ $t('messaging.briefOptIn.coverageLabel') }}</span>
        <fusion-dropdown
          v-model="form.coverage"
          name="briefCoverage"
          data-test="brief-coverage"
          :options="coverage_dropdown_options"
          :placeholder="$t('messaging.briefOptIn.placeholder')"
          :empty-text="$t('messaging.briefOptIn.noOptions')"
          :disabled="saving"
          sort-order="none"
          compact
          @change="saveCoverage"
        />
        <span v-if="coverage_cap_note" data-test="brief-coverage-cap" class="text-xs text-text-subtle">{{ coverage_cap_note }}</span>
      </label>

      <div class="flex gap-3">
        <label class="flex flex-col gap-1 flex-1">
          <span class="text-xs font-medium text-text-default">{{ $t('messaging.briefOptIn.cadenceLabel') }}</span>
          <fusion-dropdown
            v-model="form.cadence"
            name="briefCadence"
            :options="cadence_dropdown_options"
            :placeholder="$t('messaging.briefOptIn.placeholder')"
            :empty-text="$t('messaging.briefOptIn.noOptions')"
            :disabled="saving"
            sort-order="none"
            compact
            @change="saveSchedule"
          />
        </label>
        <label class="flex flex-col gap-1 w-32">
          <span class="text-xs font-medium text-text-default">{{ $t('messaging.briefOptIn.timeLabel') }}</span>
          <!-- @change, not @update:modelValue: change fires only for a different pick, so re-choosing the
               current time is not a PUT. A dropdown rather than a native time input, whose clock popup
               detached from the iframe when the host page scrolled. -->
          <fusion-dropdown
            v-model="form.time"
            name="briefTime"
            :options="time_dropdown_options"
            :placeholder="$t('messaging.briefOptIn.placeholder')"
            :empty-text="$t('messaging.briefOptIn.noOptions')"
            :disabled="saving"
            sort-order="none"
            compact
            @change="saveSchedule"
          />
        </label>
      </div>

      <!-- The zone the brief will actually fire on, shown rather than assumed — the same disclosure
           ScheduledCreate uses, and for the reason its own comment gives: the default is right almost
           always, and a picker at rest invites a change nobody needed to make. -->
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-text-default">{{ $t('messaging.briefOptIn.timezoneLabel') }}</span>
          <span class="text-xs text-text-subtle flex-1 truncate">{{ selected_timezone_label }}</span>
          <button
            type="button"
            class="text-xs text-text-link flex-shrink-0"
            @click="editing_timezone = !editing_timezone"
          >{{ editing_timezone ? $t('messaging.briefOptIn.done') : $t('messaging.briefOptIn.change') }}</button>
        </div>
        <fusion-dropdown
          v-if="editing_timezone"
          v-model="form.timeZone"
          name="briefTimeZone"
          :options="timezone_dropdown_options"
          :placeholder="$t('messaging.briefOptIn.placeholder')"
          :empty-text="$t('messaging.briefOptIn.noOptions')"
          :disabled="saving"
          sort-order="none"
          compact
          @change="saveSchedule"
        />
      </div>

      <div v-if="error_message" class="text-xs text-danger">{{ error_message }}</div>
    </div>
    </div>
  </div>
</template>

<script>
/**
 * Opting in to the morning brief, and the one setting it has: when it fires.
 *
 * WHY THIS IS ITS OWN CARD AND NOT A FOURTH TYPE CARD. ScheduledTypeCards offers Recap, Watcher and
 * Event trigger — three shapes a person fills in. A brief has nothing to fill in: its content comes
 * from the fact catalogue, so the only decisions are whether to have one and what time it lands. A
 * type card would have opened an empty form and asked a question with no answer.
 *
 * WHY THE TOGGLE IS THE OPT-IN AND NOT A CREATE BUTTON. The state being toggled is a real
 * ScheduledTask — created on first opt-in, then paused and resumed rather than deleted and recreated.
 * Deleting would throw away the task's run history and its audit trail, which is the record of what
 * Rey has shown this person; someone switching the brief off for a fortnight has not asked for that.
 *
 * The brief is deliberately EXCLUDED from the task list this card sits above (see
 * ScheduledManagement.tasks_sorted). One object with two enable switches on one scroll is a bug
 * report waiting to happen, and this card is the more specific of the two surfaces.
 */
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import FusionSwitch from '@components/UI/FusionSwitch.vue';
import FusionDropdown from '@components/UI/FusionDropdown.vue';
import { timezoneOptionsFor, timezoneDropdownOptions, browserTimeZone } from '../../utils/timezones';
import { timeOfDayOptions, padTime } from '../../utils/timeOfDay';
import { ALL_ROOFTOPS_GROUP_ID } from '../../utils/dealerGroups';

/**
 * The two cadences this card offers. Kept as i18n keys rather than text for the same reason
 * TIMEZONE_OPTIONS is: the VALUES are persisted and resolved server-side (TaskCadence), the labels are
 * not. Weekdays exists because a brief about a dealership's morning is worth less on a day the
 * dealership is shut.
 */
const CADENCE_OPTIONS = [
  { labelKey: 'messaging.briefOptIn.cadences.daily', value: 'Daily' },
  { labelKey: 'messaging.briefOptIn.cadences.weekdays', value: 'Weekdays' },
];

/**
 * The stored name. English, deliberately, while everything rendered on this card is translated.
 *
 * It is persisted and then read back on surfaces this component does not own — the results inbox, the
 * store admin inventory, a task's audit trail — where the reader may not be the person who created it.
 * A name written in the creator's locale would make a French owner's brief show up as French in an
 * English administrator's store inventory, permanently, with nothing able to re-translate it. Matches
 * the label ScheduledTaskRow already maps the type to.
 */
const BRIEF_TASK_NAME = 'Morning brief';

/**
 * Most stores one brief covers. The server enforces it (ScheduledTaskExecutor.MaxGroupBriefStores); this is
 * only so the card can say so before the morning does.
 */
const MAX_GROUP_BRIEF_STORES = 10;

/** A coverage <option> value names its kind, since a CIF and a group id share no namespace. */
const STORE_PREFIX = 'store:';
const GROUP_PREFIX = 'group:';

export default {
  name: 'MorningBriefOptIn',
  components: {
    'fusion-switch': FusionSwitch,
    'fusion-dropdown': FusionDropdown,
  },
  props: {
    /** Opting in is withdrawn — rooftop outside the beta, or a read-only caller. */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Why, in the person's own terms. Rendered where the schedule would be. */
    disabledReason: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['changed'],
  created() {
    // The groups the Covers picker offers. Cached in the store, so this costs a request only once.
    this.fetchDealerGroups();
  },
  data() {
    return {
      expanded: false,
      saving: false,
      error_message: '',
      editing_timezone: false,
      // True while the first dealer-group fetch is in flight, so a brief covering a group is not called
      // unavailable for the moment before its group arrives.
      dealer_groups_loading: false,
      form: {
        cadence: 'Daily',
        time: '07:00',
        // The zone the user is actually in, not an American default — the defect ScheduledCreate's own
        // blankForm records, where a brief asked for at 07:00 in Paris fired at 13:00 on its owner's
        // clock.
        timeZone: browserTimeZone() || 'America/New_York',
        // 'store:<cif>' or 'group:<id>'. Empty until seeded from the stored brief or the active store.
        coverage: '',
      },
    };
  },
  computed: {
    ...mapStores(useMainStore),
    current_cif() {
      return this.mainStore.current_cif;
    },
    scheduled_tasks() {
      return this.mainStore.scheduled_tasks;
    },
    /**
     * The person's brief, or null.
     *
     * One per person since 2026-10-02 (the server answers a second create with the first): the Covers
     * picker below chooses which store or dealer group it reports on, so the active rooftop no longer
     * decides which brief this card shows. Someone left with one brief per store from before then sees
     * the active store's, falling back to their first.
     */
    brief_task() {
      const tasks = Array.isArray(this.scheduled_tasks) ? this.scheduled_tasks : [];
      const briefs = tasks.filter(t => t && t.taskType === 'MorningBrief');
      return briefs.find(t => this.current_cif && t.dealerId === this.current_cif) || briefs[0] || null;
    },
    all_dealers() {
      return this.mainStore.all_dealers;
    },
    usable_dealer_groups() {
      return this.mainStore.usable_dealer_groups;
    },
    dealer_groups() {
      return this.mainStore.dealer_groups;
    },
    /** Dealerships, alphabetical, labelled the way the chat composer labels them: "Name (CIF)". */
    coverage_dealers() {
      return [...(this.all_dealers || [])]
        .filter(d => d && d.cifNumber)
        .sort((a, b) => (a.name || a.cifNumber || '').localeCompare(b.name || b.cifNumber || ''))
        .map(d => ({
          value: STORE_PREFIX + d.cifNumber,
          label: `${d.name || this.$t('common.noName')} (${d.cifNumber})`,
        }));
    },
    /** "All rooftops" first when the person has more than one store, then their usable groups. */
    coverage_groups() {
      const groups = [];
      const dealerCount = (this.all_dealers || []).length;
      if (dealerCount > 1)
        groups.push({
          value: GROUP_PREFIX + ALL_ROOFTOPS_GROUP_ID,
          label: `${this.$t('messaging.input.allRooftops')} (${dealerCount})`,
          count: dealerCount,
        });
      (this.usable_dealer_groups || []).forEach(g => groups.push({
        value: GROUP_PREFIX + g.id,
        label: `${g.name} (${g.accessibleCount})`,
        count: g.accessibleCount,
      }));
      return groups;
    },
    /**
     * What the brief covers, as an option, when the lists above no longer offer it: a group that was deleted
     * or no longer includes any of the person's stores, a store they have lost, or "All rooftops" for
     * someone down to one store. Null when the choice is on offer, or while what it would be checked against
     * is still loading — the dealer list, or the groups for a group.
     *
     * Named by what can still be known: the group's name if the store holds it, else the CIF.
     */
    coverage_unavailable() {
      const value = this.form.coverage || '';
      if (!value || !(this.all_dealers || []).length) return null;
      const offered = [...this.coverage_groups, ...this.coverage_dealers];
      if (offered.some(o => o.value === value)) return null;

      let name;
      if (value.startsWith(GROUP_PREFIX)) {
        // Still loading, or the fetch failed (the store keeps null on a failure): absence from an
        // unloaded list proves nothing, so coverage_pending shows the choice instead.
        if (this.coverage_pending) return null;
        if (this.dealer_groups_loading) return null;
        const id = value.slice(GROUP_PREFIX.length);
        const known = (this.dealer_groups || []).find(g => g && g.id === id);
        name = id === ALL_ROOFTOPS_GROUP_ID
          ? this.$t('messaging.input.allRooftops')
          : (known && known.name) || this.$t('messaging.briefOptIn.coverageUnknownGroup');
      } else {
        name = value.startsWith(STORE_PREFIX) ? value.slice(STORE_PREFIX.length) : value;
      }
      return { value, label: this.$t('messaging.briefOptIn.coverageUnavailable', { name }) };
    },
    /**
     * A dealer group the brief covers, as an option, while the person's groups are not known: still loading,
     * or the fetch failed — the store soft-fails and leaves its list null, where a loaded list is an array.
     * Neither says the group is gone, so this names it plainly where coverage_unavailable would call it
     * unavailable and invite moving the brief off a group that still works. Null for anything else, and for
     * "All rooftops", which the dealer list alone decides.
     */
    coverage_pending() {
      const value = this.form.coverage || '';
      if (!value.startsWith(GROUP_PREFIX)) return null;
      const id = value.slice(GROUP_PREFIX.length);
      if (id === ALL_ROOFTOPS_GROUP_ID) return null;
      if (!this.dealer_groups_loading && this.dealer_groups !== null) return null;
      if (this.coverage_groups.some(o => o.value === value)) return null;
      return { value, label: this.$t('messaging.briefOptIn.coverageUnknownGroup') };
    },
    show_coverage() {
      // An unavailable choice shows the picker even with nothing else to choose between, so the person
      // can see what the brief covers and move it to the store they have.
      // A pending group shows it for the same reason: the picker is where the person sees what it covers.
      return this.coverage_dealers.length > 1 || this.coverage_groups.length > 0 || !!this.coverage_unavailable
        || !!this.coverage_pending;
    },
    /** Said before the morning does: a group past the cap is covered for its first ten stores only. */
    coverage_cap_note() {
      const group = this.coverage_groups.find(g => g.value === this.form.coverage);
      if (!group || group.count <= MAX_GROUP_BRIEF_STORES) return '';
      return this.$t('messaging.briefOptIn.coverageCap', { count: group.count, max: MAX_GROUP_BRIEF_STORES });
    },
    /**
     * The coverage as the server takes it. A group keeps an anchor store — the scheduled-tasks switch and
     * the store's hours still read one — which is the brief's current store, or the active one for a new brief.
     * An empty dealerGroupId clears a group, since the server cannot tell an absent field from null.
     *
     * An existing brief's anchor is NOT re-sent with a group: null tells the server to leave it, and a store
     * that is sent is re-checked against the person's stores. Re-sending it refused a group change (403,
     * "That store is not one of yours") for anyone who had lost the brief's original store, although the
     * change did not touch the store at all.
     */
    coverage_payload() {
      const value = this.form.coverage || '';
      if (value.startsWith(GROUP_PREFIX)) {
        const anchor = this.brief_task ? '' : (this.current_cif || '');
        return { dealerId: anchor || null, dealerGroupId: value.slice(GROUP_PREFIX.length) };
      }
      const cif = value.startsWith(STORE_PREFIX) ? value.slice(STORE_PREFIX.length) : (this.current_cif || '');
      return { dealerId: cif || null, dealerGroupId: '' };
    },
    /**
     * Opted in means a brief exists AND is running. A paused brief reads as off, which is what the
     * person who paused it did — the distinction between paused and absent matters to the server (one
     * keeps its history) and to nobody looking at this switch.
     */
    opted_in() {
      return !!this.brief_task && this.brief_task.enabled !== false;
    },
    /**
     * The title bar's state, so a collapsed card still says whether the brief is on. Status only — the
     * cadence and time live in the open section, not the title bar.
     */
    status_label() {
      return this.opted_in
        ? this.$t('messaging.briefOptIn.statusOn')
        : this.$t('messaging.briefOptIn.statusOff');
    },
    /**
     * Daily and Weekdays, plus whatever the task already carries when that is neither.
     *
     * ScheduledEdit used to list MorningBrief and offers every cadence, so a brief made before this card
     * owned it can legitimately be on Weekly or Monthly. Dropping that value here would rewrite someone's
     * schedule as a side effect of opening the panel — a read turning into a write, the same failure timezoneOptionsFor exists to
     * prevent.
     */
    cadence_options() {
      const stored = this.brief_task && this.brief_task.trigger && this.brief_task.trigger.cadence;
      if (!stored || CADENCE_OPTIONS.some(c => c.value === stored)) return [...CADENCE_OPTIONS];
      return [...CADENCE_OPTIONS, { label: stored, value: stored }];
    },
    timezone_options() {
      return timezoneOptionsFor(this.form.timeZone);
    },
    // The lists above in the {value, label} shape FusionDropdown reads, i18n keys resolved.
    timezone_dropdown_options() {
      return timezoneDropdownOptions(this.timezone_options, key => this.$t(key));
    },
    // Kept on offer: the time the brief is STORED with, not the one picked since, the way cadence_options
    // reads the stored cadence, so an off-grid time does not vanish the moment something else is picked.
    time_dropdown_options() {
      const stored = this.brief_task && this.brief_task.trigger && this.brief_task.trigger.time;
      return timeOfDayOptions(stored || this.form.time);
    },
    cadence_dropdown_options() {
      return this.cadence_options.map(c => ({ value: c.value, label: c.labelKey ? this.$t(c.labelKey) : c.label }));
    },
    /**
     * The coverage picker's rows, in the order the old <select> showed them: what the brief covers when that
     * is no longer on offer, or not yet known (both shown but not choosable, so the picker never matches
     * nothing), then the groups, then the dealerships, each under its own heading.
     */
    coverage_dropdown_options() {
      const options = [];
      if (this.coverage_unavailable)
        options.push({ value: this.coverage_unavailable.value, label: this.coverage_unavailable.label, disabled: true });
      if (this.coverage_pending)
        options.push({ value: this.coverage_pending.value, label: this.coverage_pending.label, disabled: true });
      if (this.coverage_groups.length) {
        options.push({ heading: this.$t('messaging.input.groups') });
        this.coverage_groups.forEach(g => options.push({ value: g.value, label: g.label }));
      }
      options.push({ heading: this.$t('messaging.input.dealerships') });
      this.coverage_dealers.forEach(d => options.push({ value: d.value, label: d.label }));
      return options;
    },
    selected_timezone_label() {
      const match = this.timezone_options.find(z => z.value === this.form.timeZone);
      if (!match) return this.form.timeZone;
      return match.labelKey ? this.$t(match.labelKey) : match.label;
    },
  },
  watch: {
    /**
     * Seed the controls from the stored brief whenever one arrives or changes.
     *
     * Immediate because the parent fetches the task list in its own created(), so on a cold open this
     * component renders before the answer exists. Re-seeding after our own save is harmless — the
     * values being written back are the ones we just sent.
     */
    brief_task: {
      immediate: true,
      handler(task) {
        const trigger = task && task.trigger;
        if (!trigger) return;
        if (trigger.cadence) this.form.cadence = trigger.cadence;
        const time = padTime(trigger.time);
        if (time) this.form.time = time;
        if (trigger.timeZone) this.form.timeZone = trigger.timeZone;
        this.form.coverage = this.storedCoverage(task) || this.form.coverage;
      },
    },
    /**
     * Before any brief exists, the picker starts on the active store, which is what a brief created
     * without touching it has always covered.
     */
    current_cif: {
      immediate: true,
      handler(cif, previous) {
        if (!cif) return;
        // And it keeps following the active store until a brief exists, while the choice is still the one
        // seeded from the store before. The picker is hidden until the first opt-in, so a seed left behind
        // by a store switch was invisible, and the brief was created for a store the person had left.
        // Anything else — a stored brief's coverage, a deliberate choice — stays.
        const seededFromPrevious = !!previous && this.form.coverage === STORE_PREFIX + previous;
        if (!this.form.coverage || (!this.brief_task && seededFromPrevious)) this.form.coverage = STORE_PREFIX + cif;
      },
    },
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.expanded = !this.expanded;
    },
    /**
     * The switch. Three outcomes, not two: the first opt-in creates the task, every later one resumes
     * or pauses the task that already exists.
     */
    setOptedIn(next) {
      if (this.disabled || this.saving) return Promise.resolve(false);
      this.error_message = '';
      if (!next) return this.setEnabled(false);
      return this.brief_task ? this.setEnabled(true) : this.createBrief();
    },
    createBrief() {
      const self = this;
      this.saving = true;
      return this.mainStore.SaveScheduledTask(this.buildCreatePayload()).then((saved) => {
        // Added to the store's list before the switch is released, as setEnabled updates its row first.
        // The switch shows what that list holds, and the parent's refetch lands later: until it did, the
        // switch sat enabled and reading off, and a second click created a second brief. The server can
        // answer with a brief that already exists (ScheduledTasksController.Create), so a row with the
        // same id is replaced rather than listed twice.
        if (saved && saved.id && saved.taskType === 'MorningBrief') {
          const tasks = Array.isArray(self.mainStore.scheduled_tasks) ? self.mainStore.scheduled_tasks : [];
          self.mainStore.scheduled_tasks = [...tasks.filter(t => !t || t.id !== saved.id), saved];
        }
        self.saving = false;
        self.$emit('changed', 'created');
        return saved;
      }, () => {
        self.saving = false;
        // No local rollback needed: the switch reads through to the store, which only changes on a
        // successful round trip, so a failure leaves it showing the state that is actually true.
        self.error_message = self.$t('messaging.briefOptIn.error');
        return false;
      });
    },
    setEnabled(enabled) {
      const task = this.brief_task;
      if (!task) return Promise.resolve(false);

      const self = this;
      this.saving = true;
      return this.mainStore.SetScheduledTaskEnabled({ id: task.id, enabled }).then(() => {
        // Mirrors ScheduledManagement.toggleEnabled: the row the store already holds is updated in
        // place so the switch settles immediately rather than waiting on a refetch.
        task.enabled = enabled;
        self.saving = false;
        self.$emit('changed', enabled ? 'resumed' : 'paused');
        return true;
      }, () => {
        self.saving = false;
        self.error_message = self.$t('messaging.briefOptIn.error');
        return false;
      });
    },
    /**
     * Persist a cadence, time or zone change.
     *
     * Trigger-only, which the server reads as "leave everything else alone" — name, type, delivery and
     * enabled are all optional on UpdateTaskRequest and silence preserves them. Sending a whole task
     * here would mean this card deciding things it never asked the person about.
     */
    saveSchedule() {
      const task = this.brief_task;
      if (!task || this.saving) return Promise.resolve(false);

      const self = this;
      this.saving = true;
      this.error_message = '';
      return this.mainStore.SaveScheduledTask({
        id: task.id,
        trigger: {
          cadence: this.form.cadence,
          time: this.form.time,
          timeZone: this.form.timeZone,
        },
      }).then((saved) => {
        self.saving = false;
        self.$emit('changed', 'rescheduled');
        return saved;
      }, () => {
        self.saving = false;
        self.error_message = self.$t('messaging.briefOptIn.error');
        return false;
      });
    },
    /**
     * Persist what the brief covers. Store and group only — the server reads silence as "leave everything
     * else alone", the same contract saveSchedule relies on. Before a brief exists there is nothing to save:
     * the choice travels with the first opt-in instead (buildCreatePayload).
     */
    saveCoverage() {
      const task = this.brief_task;
      if (!task || this.saving) return Promise.resolve(false);

      const self = this;
      const payload = this.coverage_payload;
      this.saving = true;
      this.error_message = '';
      return this.mainStore.SaveScheduledTask({ id: task.id, ...payload }).then((saved) => {
        // The row the store holds is updated in place, as setEnabled does, so a later failed change rolls
        // back to THIS choice rather than to the one before it while the parent's refetch is in flight.
        // From what was sent, which the server applies as sent: an empty group clears, a null store keeps.
        task.dealerId = payload.dealerId || task.dealerId;
        task.dealerGroupId = payload.dealerGroupId || null;
        self.saving = false;
        self.$emit('changed', 'rescheduled');
        return saved;
      }, () => {
        self.saving = false;
        // Unlike the switch, the picker does not read through to the store: v-model has already moved it to
        // the refused choice, and nothing re-seeds it because the stored brief did not change. Put it back so
        // it shows what the brief actually covers, beside the error saying the change did not take.
        self.form.coverage = self.storedCoverage(task) || self.form.coverage;
        self.error_message = self.$t('messaging.briefOptIn.error');
        return false;
      });
    },
    /** The picker value for what a stored brief covers: its group if it has one, else its store. */
    storedCoverage(task) {
      if (!task) return '';
      if (task.dealerGroupId) return GROUP_PREFIX + task.dealerGroupId;
      return task.dealerId ? STORE_PREFIX + task.dealerId : '';
    },
    fetchDealerGroups() {
      const self = this;
      this.dealer_groups_loading = true;
      // The store's fetch soft-fails to whatever it has, so this always settles.
      return this.mainStore.fetchDealerGroups().then(() => {
        self.dealer_groups_loading = false;
      }, () => {
        self.dealer_groups_loading = false;
      });
    },
    buildCreatePayload() {
      const payload = {
        name: BRIEF_TASK_NAME,
        taskType: 'MorningBrief',
        enabled: true,
        trigger: {
          cadence: this.form.cadence,
          time: this.form.time,
          timeZone: this.form.timeZone,
        },
        // In-app only, and this records it rather than choosing it: the dispatcher's brief path posts
        // a card per finding into the brief thread and has no email or push call in it at all.
        delivery: { channels: ['InApp'] },
      };
      // Omitted rather than sent empty, so the server falls back to the caller's own CIF exactly as it
      // does for the structured editor. An empty string here is a dealer id that matches no store.
      const coverage = this.coverage_payload;
      if (coverage.dealerId) payload.dealerId = coverage.dealerId;
      if (coverage.dealerGroupId) payload.dealerGroupId = coverage.dealerGroupId;
      return payload;
      // Deliberately no `prompt` and no `condition`: a brief's content comes from the fact catalogue,
      // and the server refuses a brief that carries a condition (400).
    },
  },
};
</script>
